import { useEffect, useMemo, useState } from 'react'
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore'
import { db } from './firebase'
import type { Tour, TourCrew, WithId } from '../types/models'
import { resolvedAccessLevel } from '../types/models'

/**
 * Every tour the signed-in user can reach.
 *
 * Two sources, exactly as `AppState.loadTours` resolves them: tours they own,
 * plus tours they've accepted a crew invitation to. There is no single query
 * that answers this — ownership lives on the tour, membership lives in
 * `tourCrew` — so it is two listeners unioned by id.
 *
 * Both are live. A tour accepted on the phone appears here without a reload,
 * which is the whole point of the accounts being one account.
 */
export function useTours(userId: string | null | undefined) {
  const [owned, setOwned] = useState<WithId<Tour>[]>([])
  const [crewed, setCrewed] = useState<WithId<Tour>[]>([])
  const [loadingOwned, setLoadingOwned] = useState(true)
  const [loadingCrewed, setLoadingCrewed] = useState(true)

  useEffect(() => {
    if (!userId) {
      setOwned([])
      setLoadingOwned(false)
      return
    }
    setLoadingOwned(true)
    return onSnapshot(
      query(collection(db, 'tours'), where('ownerId', '==', userId)),
      (snapshot) => {
        setOwned(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as WithId<Tour>))
        setLoadingOwned(false)
      },
      (error) => {
        console.error('[tours] owned', error)
        setLoadingOwned(false)
      },
    )
  }, [userId])

  useEffect(() => {
    if (!userId) {
      setCrewed([])
      setLoadingCrewed(false)
      return
    }
    setLoadingCrewed(true)

    // The crew listener drives a second, nested set of tour listeners. They're
    // torn down and rebuilt whenever the accepted set changes, which is rare.
    let tourUnsubscribers: Array<() => void> = []

    const stopCrew = onSnapshot(
      query(
        collection(db, 'tourCrew'),
        where('userId', '==', userId),
        where('status', '==', 'accepted'),
      ),
      (snapshot) => {
        const tourIds = Array.from(
          new Set(snapshot.docs.map((entry) => (entry.data() as TourCrew).tourId).filter(Boolean)),
        )

        tourUnsubscribers.forEach((stop) => stop())
        tourUnsubscribers = []

        if (tourIds.length === 0) {
          setCrewed([])
          setLoadingCrewed(false)
          return
        }

        // ONE LISTENER PER TOUR rather than a chunked `in`.
        //
        // The tour rule resolves membership by reading the tour document, and
        // Firestore caps document accesses per request. A single query naming
        // twenty-six tours needs twenty-six of them and is denied outright, so
        // every crewed tour vanished at once. Constrained to one id, each
        // query needs one lookup and always fits.
        const buffer: WithId<Tour>[][] = tourIds.map(() => [])

        tourUnsubscribers = tourIds.map((tourId, index) =>
          onSnapshot(
            doc(db, 'tours', tourId),
            (tourSnapshot) => {
              buffer[index] = tourSnapshot.exists()
                ? [{ id: tourSnapshot.id, ...tourSnapshot.data() } as WithId<Tour>]
                : []
              setCrewed(buffer.flat())
              setLoadingCrewed(false)
            },
            (error) => {
              // One tour the reader has lost access to must not blank the rest.
              console.warn('[tours] crewed tour', tourId, error)
              setLoadingCrewed(false)
            },
          ),
        )
      },
      (error) => {
        console.error('[tours] crew', error)
        setLoadingCrewed(false)
      },
    )

    return () => {
      stopCrew()
      tourUnsubscribers.forEach((stop) => stop())
    }
  }, [userId])

  const tours = useMemo(() => {
    const byId = new Map<string, WithId<Tour>>()
    for (const tour of [...owned, ...crewed]) byId.set(tour.id, tour)
    return Array.from(byId.values()).sort(
      (a, b) => a.startDate.toMillis() - b.startDate.toMillis(),
    )
  }, [owned, crewed])

  return {
    tours,
    owned,
    /** Tours somebody else owns and invited you onto. */
    crewed,
    loading: loadingOwned || loadingCrewed,
  }
}

/* ============================================================================
   Access
   ============================================================================ */

export type TourAccessState =
  | { kind: 'open'; level: 'editor' | 'viewer' }
  | { kind: 'pending'; startsOn: Date }
  | { kind: 'expired'; endedOn: Date }

export interface TourAccess {
  state: TourAccessState
  /** The viewer's own `tourCrew` document id. Drives "what's mine" — which
   *  flights they're on, which hotel room is theirs. Null for the owner, who
   *  has no crew record. */
  crewId: string | null
  isOpen: boolean
  canEdit: boolean
  canManageCrew: boolean
}

export const ownerAccess: TourAccess = {
  state: { kind: 'open', level: 'editor' },
  crewId: null,
  isOpen: true,
  canEdit: true,
  canManageCrew: true,
}

/** Resolves a crew record's level and date window into what they can do now. */
export function accessFromCrew(crew: WithId<TourCrew>, now = new Date()): TourAccess {
  const start = crew.startDate?.toDate() ?? null
  const end = crew.endDate?.toDate() ?? null

  let state: TourAccessState
  if (start && now < startOfDay(start)) {
    state = { kind: 'pending', startsOn: start }
  } else if (end && now > end) {
    state = { kind: 'expired', endedOn: end }
  } else {
    state = { kind: 'open', level: resolvedAccessLevel(crew) }
  }

  const canEdit = state.kind === 'open' && state.level === 'editor'

  return {
    state,
    crewId: crew.id,
    isOpen: state.kind === 'open',
    canEdit,
    canManageCrew: canEdit,
  }
}

function startOfDay(date: Date): Date {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy
}

/**
 * The signed-in user's access to a tour.
 *
 * Owners short-circuit: they have no `tourCrew` document, and querying for one
 * would gate them out of their own tour. A signed-in non-owner with no crew
 * record is treated as an owner-equivalent rather than blocked, matching the
 * native apps — security rules are the real boundary, and a false gate is a
 * support ticket.
 */
export function useTourAccess(tour: WithId<Tour> | null, userId: string | null | undefined) {
  const [access, setAccess] = useState<TourAccess | null>(null)

  useEffect(() => {
    if (!tour || !userId) {
      setAccess(null)
      return
    }
    if (userId === tour.ownerId) {
      setAccess(ownerAccess)
      return
    }

    return onSnapshot(
      query(
        collection(db, 'tourCrew'),
        where('tourId', '==', tour.id),
        where('userId', '==', userId),
      ),
      (snapshot) => {
        const entry = snapshot.docs[0]
        if (!entry) {
          setAccess(ownerAccess)
          return
        }
        setAccess(accessFromCrew({ id: entry.id, ...entry.data() } as WithId<TourCrew>))
      },
      (error) => {
        console.error('[tours] access', error)
        setAccess(ownerAccess)
      },
    )
  }, [tour, userId])

  return access
}
